"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.fieldsOfOpportunityTrigger = exports.opportunityFieldEventName = exports.OPPORTUNITY_FIELD_NAME_PART = exports.OPPORTUNITY_FIELD_ANY_EVENT = exports.OPPORTUNITY_FIELD_EVENT_PREFIX = exports.isOpportunityStartsOn = exports.OPPORTUNITY_STARTS_ON = void 0;
const salesforceSyncCriteria_1 = require("../salesforceSyncCriteria");
/**
 * The words of the "Salesforce Opportunity" trigger that start on a change of
 * the Opportunity's name or of one of its fields (Lane 0, the contracts; the
 * plan is sf-v2-work/opps/plan/plan.md, sections 1.5 and 1.7).
 *
 * One trigger, operation `/salesforce-opportunity`, with three ways to start:
 *  - stage: the Opportunity moves to one of the chosen stages (what the
 *    trigger did before; a step saved without `startsOn` reads as this);
 *  - name: the Opportunity's name changes;
 *  - field: the chosen field changes.
 * Any of the three can have optional Filters, and every one has a "who enters"
 * choice.
 *
 * Pure, like salesforceTriggers.ts, which holds the stage family's words and
 * stays as it is until the trigger lane (Lane 4) builds the field family into
 * its event parser, card text and matcher. This file only fixes the names and
 * the settings shape those pieces and the sync agree on.
 */
exports.OPPORTUNITY_STARTS_ON = ["stage", "name", "field"];
const isOpportunityStartsOn = (value) => exports.OPPORTUNITY_STARTS_ON.includes(value);
exports.isOpportunityStartsOn = isOpportunityStartsOn;
// ---------------------------------------------------------------------------
// Event names
/**
 * A change of a field is announced under two names, as a stage change is:
 *  - `salesforce-opportunity-field-<api name in lower case>`, the specific one
 *    (not stored on the timeline), which a Field step listens to. The
 *    Opportunity's own name is `salesforce-opportunity-field-name`;
 *  - `salesforce-opportunity-field-any`, the general one (stored on the
 *    timeline, and the key a flow's emails read the trigger's data by).
 */
exports.OPPORTUNITY_FIELD_EVENT_PREFIX = "salesforce-opportunity-field-";
/** Every field change, whatever the field. */
exports.OPPORTUNITY_FIELD_ANY_EVENT = `${exports.OPPORTUNITY_FIELD_EVENT_PREFIX}any`;
/**
 * The part of a specific event name after the prefix: the field's API name in
 * lower case. Letters, digits and underscores, 80 characters at most (the API
 * name rule), starting with a letter. Custom fields (`My_Field__c`) and
 * namespaced ones (`ns__My_Field__c`) fit; a dotted name does not, and such a
 * field (Owner.Name, Account.Industry) cannot start a flow anyway.
 */
exports.OPPORTUNITY_FIELD_NAME_PART = /^[a-z][a-z0-9_]{0,79}$/;
/**
 * The specific event name of a field, from its API name in any case. Throws for
 * a name that cannot be an event name, and for "any", which would be the
 * general event (no Opportunity field has that API name: a custom field ends in
 * `__c`).
 */
const opportunityFieldEventName = (field) => {
    const part = typeof field === "string" ? field.toLowerCase() : "";
    if (!exports.OPPORTUNITY_FIELD_NAME_PART.test(part) || part === "any") {
        throw new Error("Not a Salesforce Opportunity field that can start a flow (letters, digits and underscores)");
    }
    return `${exports.OPPORTUNITY_FIELD_EVENT_PREFIX}${part}`;
};
exports.opportunityFieldEventName = opportunityFieldEventName;
// ---------------------------------------------------------------------------
// The fields a trigger reads
/** An object kept as an object, or as JSON text (how a form keeps one); anything else is nothing. */
const asObject = (raw) => {
    let value = raw;
    if (typeof value === "string") {
        try {
            value = JSON.parse(value);
        }
        catch (e) {
            return undefined;
        }
    }
    return typeof value === "object" && value !== null && !Array.isArray(value)
        ? value
        : undefined;
};
/** A field API name Salesforce takes in a query: no dots (a related field cannot start a flow). */
const WATCHED_FIELD_NAME = /^[A-Za-z][A-Za-z0-9_]{0,79}$/;
/**
 * The fields one Opportunity trigger reads, from its settings (or from the
 * handlerData of its listener row, which is the same object; anything else it
 * may hold is ignored).
 *  - `watched`: the fields whose change starts the flow: `Name` for a name
 *    trigger, the chosen field for a field trigger, none for a stage trigger.
 *    A `field` left over from an earlier choice of another start is not read.
 *  - `filters`: the fields its Filters use, once each (names compare without
 *    regard to case), spelled as the first rule that names them spells them.
 *    They may be related fields (Owner.Name, Account.Industry).
 *
 * Never throws: settings that cannot be read give empty lists (the trigger
 * reader refuses them at switch-on; the sync only asks what to read).
 */
const fieldsOfOpportunityTrigger = (settings) => {
    var _a, _b, _c;
    const values = (_a = asObject(settings)) !== null && _a !== void 0 ? _a : {};
    const startsOn = (0, exports.isOpportunityStartsOn)(values.startsOn)
        ? values.startsOn
        : "stage";
    const watched = [];
    if (startsOn === "name") {
        watched.push("Name");
    }
    else if (startsOn === "field") {
        const name = (_b = asObject(values.field)) === null || _b === void 0 ? void 0 : _b.name;
        if (typeof name === "string" && WATCHED_FIELD_NAME.test(name)) {
            watched.push(name);
        }
    }
    const rules = (_c = asObject(values.filters)) === null || _c === void 0 ? void 0 : _c.rules;
    const filters = rules === undefined || rules === null
        ? []
        : (0, salesforceSyncCriteria_1.fieldsOf)((0, salesforceSyncCriteria_1.readSideOf)({ rules }));
    return { watched, filters };
};
exports.fieldsOfOpportunityTrigger = fieldsOfOpportunityTrigger;
//# sourceMappingURL=opportunityTrigger.js.map